import {
  AttributeRef,
  BooleanLiteral,
  FunctionName,
  MetadataRef,
  NumberLiteral,
  StringLiteral,
  TYPE_LABELS,
} from 'js/lib/TransformDSLParser';
import { getContextAt, getFuncRule, getParamDef } from 'js/lib/TransformDSLUtils';
import * as monaco from 'monaco-editor';

// Monaco workers served from public folder (copied from node_modules/monaco-editor/esm/vs/)
// These are ES modules loaded as module workers
const getWorkerPath = (label: string): string => {
  switch (label) {
    case 'json':
      return '/monaco-workers/json.worker.js';
    case 'css':
    case 'scss':
    case 'less':
      return '/monaco-workers/css.worker.js';
    case 'html':
    case 'handlebars':
    case 'razor':
      return '/monaco-workers/html.worker.js';
    case 'typescript':
    case 'javascript':
      return '/monaco-workers/ts.worker.js';
    default:
      return '/monaco-workers/editor.worker.js';
  }
};

// Configure Monaco environment with local bundled workers
if (typeof window !== 'undefined') {
  (window as any).MonacoEnvironment = {
    getWorker: function (_moduleId: string, label: string) {
      const workerPath = getWorkerPath(label);
      // Workers are bundled as IIFE (not ES modules) by esbuild
      return new Worker(workerPath);
    },
  };
}

const LANGUAGE_ID = 'transform-dsl';
const DETAIL_SPACING = '  ';

let completionProvider: monaco.IDisposable;

export const registerTransformDSL = () => {
  monaco.languages.register({ id: LANGUAGE_ID });
  monaco.languages.setLanguageConfiguration(LANGUAGE_ID, {
    brackets: [
      ['{', '}'],
      ['[', ']'],
      ['(', ')'],
    ],
    autoClosingPairs: [
      { open: '{', close: '}' },
      { open: '[', close: ']' },
      { open: '(', close: ')' },
      { open: '"', close: '"' },
      { open: "'", close: "'" },
    ],
  });

  // syntax highlighting
  monaco.languages.setMonarchTokensProvider(LANGUAGE_ID, {
    ignoreCase: true,
    tokenizer: {
      root: [
        // function names
        [FunctionName.PATTERN as RegExp, 'function'],
        // attribute references
        [AttributeRef.PATTERN as RegExp, 'attribute.reference'],
        // metadata references
        [MetadataRef.PATTERN as RegExp, 'metadata.reference'],
        // strings
        [StringLiteral.PATTERN as RegExp, 'string'],
        // numbers
        [NumberLiteral.PATTERN as RegExp, 'number'],
        // booleans
        [BooleanLiteral.PATTERN as RegExp, 'boolean'],
        // assignment
        [/^.+(?==)/, 'attribute.assignment'],
      ],
    },
  });

  monaco.editor.defineTheme('dslTheme', {
    base: 'hc-light',
    inherit: true,
    rules: [
      { token: 'attribute', foreground: '#936a03', fontStyle: 'bold' },
      { token: 'attribute.assignment', foreground: '#936a03' },
      { token: 'metadata', foreground: '#936a03', fontStyle: 'bold' },
      { token: 'string', foreground: '#0451a5' },
      { token: 'number', foreground: '#0451a5' },
      { token: 'boolean', foreground: '#0451a5' },
      { token: 'function', foreground: '#67a1ff' },
      { token: 'delimiter', foreground: '#808080' },
    ],
    colors: {},
  });

  // Dark-based theme for DSL editor - use this to avoid conflicts with other
  // dark-themed Monaco editors on the same page
  monaco.editor.defineTheme('dslThemeDark', {
    base: 'vs-dark',
    inherit: true,
    rules: [
      // Adjusted colors for dark background
      { token: 'attribute', foreground: 'dbb070', fontStyle: 'bold' },
      { token: 'attribute.assignment', foreground: 'dbb070' },
      { token: 'attribute.reference', foreground: 'dbb070', fontStyle: 'bold' },
      { token: 'metadata', foreground: 'dbb070', fontStyle: 'bold' },
      { token: 'metadata.reference', foreground: 'dbb070', fontStyle: 'bold' },
      { token: 'string', foreground: 'ce9178', fontStyle: 'bold' },
      { token: 'number', foreground: 'b5cea8' },
      { token: 'boolean', foreground: '569cd6' },
      { token: 'function', foreground: 'dcdcaa' },
      { token: 'delimiter', foreground: '808080' },
    ],
    colors: {
      // Transparent background so TransformDSLEditor's CSS can control it
      'editor.background': '#00000000',
    },
  });
};

export const registerTransformDSLCompletion = (
  rules: any[],
  inputSchema = null,
  outputSchema = null,
  metadataSpec = null,
  transforms = null,
  dataMaps = null,
  dataMapSchemas = null,
) => {
  // separate out functions that don't require a destination attribute from those
  // that do
  const rulesWithDest: any[] = [];
  const rulesWithoutDest: any[] = [];
  const rulesByName: Record<string, any> = {};

  rules.forEach((rule: any) => {
    if (rule.withOrWithoutDest) {
      rulesWithoutDest.push(rule);
      rulesWithDest.push(rule);
    } else if (rule.ignoreDest) {
      rulesWithoutDest.push(rule);
    } else {
      rulesWithDest.push(rule);
    }

    rulesByName[rule.value] = rule;
  });

  // dispose the previous provider before registering a new one
  completionProvider?.dispose();

  completionProvider = monaco.languages.registerCompletionItemProvider(LANGUAGE_ID, {
    triggerCharacters: ['$', '(', '[', ',', ' ', '=', '"', "'"],
    provideCompletionItems: (model, position) => {
      const word = model.getWordUntilPosition(position);
      const content = model.getValue();
      const currentIndex = model.getOffsetAt(position) - 1;
      const contentUntilPosition = content.substring(0, currentIndex + 1);
      const contentAfterPosition = content.substring(currentIndex + 1);
      const lineContent = model.getLineContent(position.lineNumber);
      const lineContentUntilPosition = lineContent.substring(0, position.column - 1);

      const { inString, currentFunc, currentArgIndex, destAttr } = getContextAt(
        content,
        currentIndex,
      );

      // if the content is empty or if the cursor is before "="
      if (content.trim().replace(/\w+$/, '') === '' || content.indexOf('=') > position.column - 1) {
        const range = {
          startLineNumber: position.lineNumber,
          endLineNumber: position.lineNumber,
          startColumn: 1,
          endColumn: content.length + 1,
        };

        // suggest functions that don't require a destination attribute
        const suggestions: monaco.languages.CompletionItem[] = rulesWithoutDest.map((rule) => ({
          ...getFuncSuggestion(rule),
          range,
        }));

        return { suggestions };
      }

      // for functions, the range is the current word plus the preceding words
      // (function names can be multiple words but can't span multiple lines)
      const funcNameMatch = lineContentUntilPosition.match(/[a-zA-Z_][\w\s]*$/);
      const funcNameStartCol = funcNameMatch ? funcNameMatch.index + 1 : word.startColumn;
      const funcRange = {
        startLineNumber: position.lineNumber,
        endLineNumber: position.lineNumber,
        startColumn: funcNameStartCol,
        endColumn: word.endColumn,
      };

      // for attributes, the range is from the last "$(" to the ")" after the
      // current position
      const getAttrRangeStartCol = (prefixPattern: RegExp) => {
        let attrRangeStartCol = word.startColumn;
        const lineContentUntilWord = lineContentUntilPosition.substring(0, word.startColumn);

        const attrPrefixMatch = lineContentUntilWord.match(prefixPattern);

        if (attrPrefixMatch) {
          attrRangeStartCol = attrPrefixMatch.index + 1;
        }

        return attrRangeStartCol;
      };

      const attrRangeEndCol =
        position.column + (contentAfterPosition.match(/^\w*\)/)?.[0].length || 0);
      const attrRange = {
        startLineNumber: position.lineNumber,
        endLineNumber: position.lineNumber,
        startColumn: getAttrRangeStartCol(/\$\(?\s*$/),
        endColumn: attrRangeEndCol,
      };

      // for metadata, the range is from the last "$$(" to the ")" after the
      // current position
      const metadataRange = {
        startLineNumber: position.lineNumber,
        endLineNumber: position.lineNumber,
        startColumn: getAttrRangeStartCol(/\$\$?\(?\s*$/),
        endColumn: attrRangeEndCol,
      };

      // if after the "=" sign (which is not part of an attr ref), suggest
      // functions that require a destination attribute
      const funcSuggestionsByName = rulesWithDest.reduce((acc, rule) => {
        acc[rule.value] = {
          ...getFuncSuggestion(rule),
          range: funcRange,
        };

        return acc;
      }, {});
      const funcSuggestions = Object.values(funcSuggestionsByName) as any[];

      Object.values(transforms || {}).forEach((transform: any) => {
        if (transform.output_type === 'attribute') {
          const suggestion = getCustomTxSuggestion(transform);
          funcSuggestions.push({
            ...suggestion,
            range: funcRange,
          });
        }
      });

      const contentUntilPositionTrimmed = contentUntilPosition.trim();

      if (
        contentUntilPositionTrimmed.endsWith('=') &&
        !/\$\s*\([^)]+$/.test(contentUntilPositionTrimmed)
      ) {
        const suggestions = funcSuggestions;

        return { suggestions };
      }

      // build attribute suggestions
      const addedOutputAttrs = {};
      const attrSuggestions = [];

      outputSchema?.forEach(({ __name }) => {
        addedOutputAttrs[__name] = true;

        if (__name !== destAttr) {
          attrSuggestions.push({
            ...getAttrSuggestion(__name, 'Output'),
            range: attrRange,
          });
        }
      });

      inputSchema?.forEach(({ __name }) => {
        if (!addedOutputAttrs[__name]) {
          attrSuggestions.push({
            ...getAttrSuggestion(__name, 'Input'),
            range: attrRange,
          });
        }
      });

      Object.entries(metadataSpec || {}).forEach(([name, spec]: [name: string, spec: any]) => {
        const suggestion = getAttrSuggestion(name, 'Metadata', '$$');

        attrSuggestions.push({
          ...suggestion,
          documentation: { value: spec.description },
          range: metadataRange,

          // use a different sort text to ensure metadata suggestions appear
          // after attribute suggestions
          sortText: suggestion.label.label.replace(/^\$\$/, '%%'),
        });
      });

      // if we're in a function argument, get the function definition and
      // suggest appropriate values for the argument
      if (currentFunc && currentArgIndex >= 0) {
        const funcRule = getFuncRule(currentFunc, rulesByName);

        if (funcRule) {
          const suggestions = [];
          const paramDef = getParamDef(funcRule, currentArgIndex);

          // if the argument is a lookup, suggest lookups
          if (['lookup-nosecondary', 'lookup'].includes(paramDef?.inputElement)) {
            if (!dataMaps) {
              return { suggestions: [] };
            }

            const range = {
              startLineNumber: position.lineNumber,
              endLineNumber: position.lineNumber,
              startColumn: word.startColumn,
              endColumn: word.endColumn,
            };
            const dataMapSuggestions = Object.values<any>(dataMaps).map(({ id, name }) => ({
              label: {
                label: `${name} (ID: ${id})`,
                detail: `${DETAIL_SPACING}Lookup`,
              },
              kind: monaco.languages.CompletionItemKind.Interface,
              insertText: String(id),
              filterText: `${name} (ID: ${id})`,
              range,
            }));

            return { suggestions: dataMapSuggestions };
          }

          // in a string without predefined values, don't show any suggestions
          if (inString && !paramDef?.values?.length) {
            return { suggestions: [] };
          }

          // add suggestions based on allowed types
          const typesAllowed = paramDef?.typesAllowed || [
            'attributeRef',
            'systemAttributeRef',
            'function',
          ];

          if (
            typesAllowed.includes('attributeRef') ||
            typesAllowed.includes('systemAttributeRef')
          ) {
            suggestions.push(...attrSuggestions);
          }

          if (typesAllowed.includes('function') || paramDef?.allowedRules) {
            let filteredFuncSuggestions: monaco.languages.CompletionItem[];

            // limit suggestions to only the allowed functions if specified
            if (paramDef?.allowedRules) {
              const allowedRules = Object.values(paramDef.allowedRules)
                .map(({ optionsOrder }) => optionsOrder)
                .flat();

              filteredFuncSuggestions = allowedRules.map((ruleName) => ({
                ...funcSuggestionsByName[ruleName],

                // these functions should appear above the attributes
                sortText: `!${funcSuggestionsByName[ruleName].sortText}`,
              }));
            } else {
              // eqAttr should not appear in functions which aren't top-level
              filteredFuncSuggestions = funcSuggestions.filter(
                (suggestion) => suggestion !== funcSuggestionsByName.eqAttr,
              );
            }

            suggestions.push(...filteredFuncSuggestions);
          }

          // add predefined string suggestions or in the case of a lookup, its
          // secondary keys
          if (typesAllowed.includes('string')) {
            const quote = lineContentUntilPosition.charAt(word.startColumn - 2) === "'" ? "'" : '"';
            const startColumn =
              lineContentUntilPosition.charAt(word.startColumn - 2) === quote
                ? word.startColumn - 1
                : word.startColumn;
            let values;

            if (paramDef?.values?.length) {
              values = paramDef.values;
            } else if (paramDef?.inputElement === 'lookup-secondary-key') {
              const dataMapId = lineContentUntilPosition.match(/(\d+),\s*$/)?.[1];
              values = dataMapSchemas?.[dataMapId]?.secondaryProperties;
            }

            values?.forEach((value: string) => {
              const text = `${quote}${value}${quote}`;
              suggestions.push({
                label: { label: text, detail: `${DETAIL_SPACING}String` },
                kind: monaco.languages.CompletionItemKind.Text,
                insertText: text,
                filterText: text,
                range: {
                  startLineNumber: position.lineNumber,
                  endLineNumber: position.lineNumber,
                  startColumn,
                  endColumn:
                    position.column + (contentAfterPosition.match(/^\w*['"]/)?.[0].length || 0),
                },
              });
            });
          }

          return { suggestions };
        }
      }

      if (inString) {
        return { suggestions: [] };
      }

      // default case: function suggestions
      return { suggestions: funcSuggestions };
    },
  });
};

const getFuncSuggestion = (rule: any) => {
  const {
    label,
    value,
    help,
    requiredParams = [],
    optionalParams = [],
    params: ruleParams = [],
    __srcAttr,
  } = rule;
  const funcName = typeof label === 'object' && label.label ? label.label : label;
  const allRuleParams = [...ruleParams, ...optionalParams];
  const ruleParamsByName = allRuleParams.reduce((acc: any, param: any) => {
    acc[param.name] = param;
    return acc;
  }, {});
  const paramDocs = [];
  const paramsInsertTextParts = [];
  const optionalParamNames = optionalParams.map(({ name }) => name);
  const ruleParamNames = [...requiredParams, ...optionalParamNames];

  ruleParamNames.forEach((param: string, index: number) => {
    const num = index + 1;
    const paramDef = param === '__srcAttr' ? __srcAttr : ruleParamsByName[param];
    const lowerName = paramDef?.display_name?.toLowerCase().trim();
    const paramLabel = lowerName.replace(/\s+/g, '_').replace(/[^\w+]/g, '') || `param${num}`;

    // insert text
    const paramPlaceholder = `\${${num}:${paramLabel}}`;

    if (paramDef?.inputElement === 'object') {
      const pair1 = `\${${num}:key1}: \${${num + 1}:"value1"}`;
      const pair2 = `\${${num + 2}:key2}: \${${num + 3}:"value2"}`;

      paramsInsertTextParts.push(`{ ${pair1}, ${pair2} }`);
    } else if (paramDef?.inputElement === 'array') {
      paramsInsertTextParts.push(`[ ${paramPlaceholder} ]`);
    } else {
      paramsInsertTextParts.push(paramPlaceholder);
    }

    // doc
    let paramDoc = `**${paramLabel}**`;

    if (['lookup-nosecondary', 'lookup'].includes(paramDef?.inputElement)) {
      paramDoc += ': Lookup ID';
    } else if (paramDef?.inputElement === 'object') {
      paramDoc += ': Key-Value Pair';
    } else if (paramDef?.inputElement === 'array') {
      paramDoc += ': Array';
    } else {
      const typesAllowed =
        paramDef?.typesAllowed?.length > 0
          ? paramDef.typesAllowed
          : ['attributeRef', 'systemAttributeRef', 'function'];
      paramDoc += `: ${typesAllowed.map((type: string) => `*${TYPE_LABELS[type]}*`).join(' | ')}`;
    }

    if (paramDef?.desc) {
      paramDoc += `\n\n${paramDef.desc}`;
    }

    paramDocs.push(paramDoc);
  });
  const paramsInsertText = paramsInsertTextParts.join(', ') || '';

  return {
    label: { label: funcName },
    kind: monaco.languages.CompletionItemKind.Function,
    insertText: `${funcName} ( ${paramsInsertText}${paramsInsertText ? ' ' : ''})`,
    insertTextRules: monaco.languages.CompletionItemInsertTextRule.InsertAsSnippet,
    documentation: {
      value:
        help.desc +
        (paramDocs.length > 0
          ? '\n\n&nbsp;\n\n**INPUT PARAMETERS**\n\n' + paramDocs.join('\n\n')
          : '') +
        (help.ex ? '\n\n&nbsp;\n\n**EXAMPLE**\n\n' + help.ex : ''),
    },

    // eqAttr should appear first in function suggestions
    sortText: value === 'eqAttr' ? `AAA${funcName}` : funcName,
  };
};

const getCustomTxSuggestion = (transform) => {
  const { id, name, custom_config } = transform;
  const params = custom_config?.ui?.txArgs?.items || [];
  const rule = {
    value: `customAttrTx_${id}`,
    label: {
      label: `Tx_${id}`,
      detail: `${DETAIL_SPACING}Function`,
    },
    help: {
      desc: `Apply the custom attribute transform ${name} (ID: ${id}).`,
      ex: '',
    },
    requiredParams: params.map(({ name }) => name),
    ignoreSrc: true,
    params: params.map(({ name }) => ({
      name,
      display_name: name,
      desc: name,
      typesAllowed: ['attributeRef', 'string', 'function', 'systemAttributeRef'],
      inputElement: 'text',
      format: 'string',
      default: {
        string: '',
      },
    })),
  };
  const suggestion = getFuncSuggestion(rule);
  suggestion.label.label += ` (${name})`;

  // set 'zzz' prefix to sort text to ensure custom attr transforms appear last
  // on function suggestions
  suggestion.sortText = `zzz${name}`;

  return suggestion;
};

const getAttrSuggestion = (name, detail, prefix = '$') => {
  return {
    // without this invisible char \u200B, the label doesn't show up sometimes
    label: {
      label: `${prefix}(${name}\u200B)`,
      detail: `${DETAIL_SPACING}${detail}`,
    },
    kind: monaco.languages.CompletionItemKind.Constant,
    insertText: `${prefix}(${name})`,
  };
};
